import { config } from '../config/environment';
import { logger } from './logger';

const CLIENTS_SERVICE_URL = config.clientsApiBase || 'http://localhost:7077';

const getAccessToken = async () => {
  // First try to use the global token getter set up by useAuthenticatedApi hook
  if (typeof window !== 'undefined' && window.getAccessToken) {
    try {
      // Determine the correct scope based on environment
      const scope = config.isProduction 
        ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-prod.access_as_user`
        : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-dev.access_as_user`;
      
      return await window.getAccessToken(scope);
    } catch (error) {
      logger.warn('Global getAccessToken failed, falling back to direct MSAL:', error);
    }
  }
  
  // Fallback to direct MSAL access for non-React contexts
  const { msalInstance } = await import('../config/msalConfig');
  
  // Determine the correct scope based on environment
  const environment = process.env.NODE_ENV || 'development';
  const scope = environment === 'production' 
    ? `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-prod.access_as_user`
    : `api://${process.env.REACT_APP_AZURE_AD_CLIENT_ID}/clients-dev.access_as_user`;
  
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    throw new Error('No authenticated user found. Please log in first.');
  }
  
  const account = accounts[0];
  
  try {
    // Try to get token silently first with clients-specific scope
    const response = await msalInstance.acquireTokenSilent({
      scopes: [scope],
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    // If silent acquisition fails, try interactive method
    if (error.errorCode === 'consent_required' || 
        error.errorCode === 'interaction_required' || 
        error.errorCode === 'login_required') {
      try {
        const response = await msalInstance.acquireTokenPopup({
          scopes: [scope],
        });
        return response.accessToken;
      } catch (interactiveError) {
        throw new Error(`Failed to acquire access token: ${interactiveError.message}`);
      }
    }
    throw new Error(`Failed to acquire access token: ${error.message}`);
  }
};

// Stakeholder API functions
export const fetchStakeholders = async (options = {}) => {
  try {
    const accessToken = await getAccessToken();
    
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.pageSize) queryParams.append('pageSize', options.pageSize);
    if (options.parentCompanyId) queryParams.append('parentCompanyId', options.parentCompanyId);
    if (options.isInternal !== undefined) queryParams.append('isInternal', options.isInternal);
    if (options.isPublic !== undefined) queryParams.append('isPublic', options.isPublic);
    if (options.ownerId) queryParams.append('ownerId', options.ownerId);

    const response = await fetch(`${CLIENTS_SERVICE_URL}/stakeholders?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch stakeholders: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching stakeholders:', error);
    throw error;
  }
};

export const fetchStakeholderById = async (stakeholderId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}stakeholders/${stakeholderId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch stakeholder: ${response.status}`);
    }

    const result = await response.json();
    return result.stakeholder;
  } catch (error) {
    logger.error('Error fetching stakeholder:', error);
    throw error;
  }
};

export const createStakeholder = async (stakeholderData) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}stakeholders`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(stakeholderData),
    });

    if (!response.ok) {
      throw new Error(`Failed to create stakeholder: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error creating stakeholder:', error);
    throw error;
  }
};

export const updateStakeholder = async (stakeholderId, stakeholderData) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}stakeholders/${stakeholderId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(stakeholderData),
    });

    if (!response.ok) {
      throw new Error(`Failed to update stakeholder: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error updating stakeholder:', error);
    throw error;
  }
};

export const deleteStakeholder = async (stakeholderId) => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}stakeholders/${stakeholderId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete stakeholder: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error deleting stakeholder:', error);
    throw error;
  }
};

export const addStakeholderToCompany = async (companyId, stakeholderId, role = 'viewer') => {
  try {
    const accessToken = await getAccessToken();
    
    const response = await fetch(`${CLIENTS_SERVICE_URL}companies/${companyId}/stakeholders`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ stakeholderId, role }),
    });

    if (!response.ok) {
      throw new Error(`Failed to add stakeholder to company: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error adding stakeholder to company:', error);
    throw error;
  }
};

export const fetchUserStakeholders = async (options = {}) => {
  try {
    const accessToken = await getAccessToken();
    
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.pageSize) queryParams.append('pageSize', options.pageSize);
    if (options.isInternal !== undefined) queryParams.append('isInternal', options.isInternal);
    if (options.isPublic !== undefined) queryParams.append('isPublic', options.isPublic);

    const response = await fetch(`${CLIENTS_SERVICE_URL}stakeholders/user/list?${queryParams}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user stakeholders: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error('Error fetching user stakeholders:', error);
    throw error;
  }
};
